'use client'
import React, {useEffect, useState, useRef, useCallback} from "react";
import classes from "./klantenkaart-overmkg.module.scss";
import {
    GoogleMap,
    InfoWindow,
    Circle,
    useJsApiLoader
} from '@react-google-maps/api';
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import {useTekstClient} from "hooks/useTekstClient";
import { useSearchParams} from "next/navigation";
import Bugsnag from "@bugsnag/js";
import Switch from "react-switch";

const TYPE_COLOR_CLASSES = {
    10: { // Klanten - default blue-dot.svg colors
        marker: '',
        cluster: '',
    },
    11: { // Sales/Consultancy - green
        marker: 'filter hue-rotate-[-95deg] saturate-[50%] brightness-[140%]',
        cluster: 'filter hue-rotate-[-75deg] saturate-[150%] brightness-[110%]',
    },
    12: { // Add-on Partners - subtle yellow
        marker: 'filter hue-rotate-[-140deg] saturate-[40%] brightness-[150%]',
        cluster: 'filter hue-rotate-[-155deg] saturate-[100%] brightness-[160%]',
    },
}

const TYPE_SWITCH_COLORS = {
    10: '#02AFF1', // Klanten - default blue-dot.svg fill color
    11: '#8CE460', // Sales/Consultancy - subtle green
    12: '#F1D873', // Add-on Partners - subtle yellow
}

export const KlantenkaartOverMKG = () => {
    const searchParams = useSearchParams();
    const kmParam = searchParams.get("km")
    const plaatsParam = searchParams.get("plaats")
    const {tekst} = useTekstClient()
    const [selectedKlant, setSelectedKlant] = useState();
    const [locatie, setLocatie] = useState();
    const [adres, setAdres] = useState();
    const [centerMap, setCenterMap] = useState({
        lat: 52.132633, lng: 5.291266
    })
    const [zoomNumber, setZoomNumber] = useState(7)

    const [selectedPlaats, setSelectedPlaats] = useState();
    const [selectedAfstand, setSelectedAfstand] = useState(30);
    const [rerender, setRerender] = useState(true)
    const [showCircle, setShowCircle] = useState(false);
    const [centerCircle, setCenterCircle] = useState();
    const [radiusCircle, setRadiusCircle] = useState();

    const [klantenlijst, setKlantenlijst] = useState();
    const [klantenlijstKolommen, setKlantenlijstKolommen] = useState([]);
    const [mapReady, setMapReady] = useState(false);

    const BEDRIJF_TYPE_OPTIONS = [
        {id: 10, label: tekst("pagina:overmkg:klantenkaart:klanten")},
        {id: 11, label: tekst('pagina:overmkg:klantenkaart:sales')},
        {id: 12, label: tekst('pagina:overmkg:klantenkaart:partners')},
    ]

    // All types active by default. At least one must always stay active.
    const [activeTypes, setActiveTypes] = useState(
        Object.fromEntries(BEDRIJF_TYPE_OPTIONS.map(({id}) => [id, true]))
    );

    const onToggleType = (id) => {
        setActiveTypes(prev => {
            const nextValue = !prev[id];
            const aantalActief = Object.values(prev).filter(Boolean).length;

            // Guard: er moet altijd minstens 1 type actief blijven.
            if (!nextValue && aantalActief <= 1) {
                return prev;
            }

            return {...prev, [id]: nextValue};
        });
    }

    const mapRef = useRef(null);
    const markersRef = useRef([]);
    const clustererRef = useRef(null);

    const center = {
        lat: 52.132633, lng: 5.291266
    }

    const containerStyle = {
        width: "100%",
        height: "425px"
    };

    const clusterStyle = [
        {
            url: '/media/google_maps_icons/m1.svg',
            textColor: '#ffffff',
            textSize: 10,
            width: 48,
            height: 48,
        },
        {
            url: '/media/google_maps_icons/m2.svg',
            textColor: '#ffffff',
            textSize: 10,
            width: 52,
            height: 52,
        },
        {
            url: '/media/google_maps_icons/m3.svg',
            textColor: '#ffffff',
            textSize: 10,
            width: 62,
            height: 62,
        },
    ]

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_API_KEY,
        libraries: ['maps', 'marker']
    })

    const onMapLoad = useCallback((map) => {
        mapRef.current = map;
        setMapReady(true);
    }, []);

    const onMapUnmount = useCallback(() => {
        mapRef.current = null;
        setMapReady(false);
    }, []);


    useEffect(() => {
        const abortController = new AbortController()

        const activeIds = Object.entries(activeTypes)
            .filter(([, active]) => active)
            .map(([id]) => id)

        const query = activeIds.map(id => `types[]=${id}`).join('&')

        fetch(`${process.env.NEXT_PUBLIC_BACKEND}/api/klantenkaart/bedrijven?${query}`, {
            credentials: "include",
            signal: abortController.signal,
        }).then(response => response.json())
            .then((data) => {

                let prevChar = "";
                data.forEach((d, index) => {
                    if (prevChar !== d.naam.charAt(0).toUpperCase()) {
                        prevChar = d.naam.charAt(0).toUpperCase();
                        const obj = {
                            adres: "",
                            id: null,
                            latitude: "",
                            longitude: "",
                            naam: d.naam.charAt(0).toUpperCase(),
                            url: ""
                        }
                        data.splice(index, 0, obj)
                    }
                })

                data.forEach((t, index) => {
                    if (prevChar !== t.naam.charAt(0).toUpperCase() && (t.naam.charAt(0).toUpperCase() === "Y" || t.naam.charAt(0).toUpperCase() === "X")) {
                        prevChar = t.naam.charAt(0).toUpperCase();
                        const obj = {
                            adres: "",
                            id: null,
                            latitude: "",
                            longitude: "",
                            naam: t.naam.charAt(0).toUpperCase(),
                            url: ""
                        }
                        data.splice(index, 0, obj)
                    }
                })
                data.forEach((t, index) => {
                    if (prevChar !== t.naam.charAt(0).toUpperCase() && t.naam.charAt(0).toUpperCase() === "Z") {
                        prevChar = t.naam.charAt(0).toUpperCase();
                        const obj = {
                            adres: "",
                            id: null,
                            latitude: "",
                            longitude: "",
                            naam: t.naam.charAt(0).toUpperCase(),
                            url: ""
                        }
                        data.splice(index, 0, obj)
                    }
                })
                setKlantenlijst(data)
            })
            .catch((error) => {
                // Genegeerd: request is bewust geannuleerd omdat de filters
                // ondertussen gewijzigd zijn (zie cleanup hieronder).
                if (error.name === 'AbortError') return
                Bugsnag.notify(error)
            });

        return () => abortController.abort()
    }, [activeTypes])


    useEffect(() => {
        if (!klantenlijst || !selectedKlant?.id) return
        const nogAanwezig = klantenlijst.some((klant) => klant.id === selectedKlant.id)
        if (!nogAanwezig) {
            onCloseClick()
        }
    }, [klantenlijst])


    useEffect(() => {
        if (!klantenlijst) return
        const data = [...klantenlijst]

        const aantalBedrijven = data.filter(d => d.id !== null).length
        const aantalKolommen = Math.min(3, Math.max(1, Math.ceil(aantalBedrijven / 10)))
        const chunk = Math.ceil(data.length / aantalKolommen)

        const kolommen = []
        for (let i = 0; i < aantalKolommen; i++) {
            kolommen.push(data.splice(0, chunk))
        }

        setKlantenlijstKolommen(kolommen)
    }, [klantenlijst])


    useEffect(() => {
        if (plaatsParam) {
            fromAddress(plaatsParam)
                .then(({lat, lng}) => {
                    setCenterCircle({
                        lat: lat,
                        lng: lng
                    })
                    setCenterMap({
                        lat: lat,
                        lng: lng
                    })
                    if (kmParam === 30) {
                        setZoomNumber(9)
                    } else if (kmParam === 50) {
                        setZoomNumber(8)
                    } else if (kmParam === 100) {
                        setZoomNumber(7)
                    }
                    setRadiusCircle(kmParam * 1000)
                    setShowCircle(true)
                    setSelectedPlaats(plaatsParam)
                    setSelectedAfstand(kmParam)
                })
        }
    }, [])

    const fromAddress = (address) =>
        fetch(
            `https://geocode.googleapis.com/v4/geocode/address/${encodeURIComponent(address)}?regionCode=NL&key=${process.env.NEXT_PUBLIC_GOOGLE_API_KEY}`
        )
            .then((res) => res.json())
            .then(({results}) => {
                const {latitude, longitude} = results[0].location;
                return {lat: latitude, lng: longitude};
            })


    const onSelect = useCallback((klant) => {
        setSelectedKlant(klant)
        setLocatie({lat: parseFloat(klant['latitude']), lng: parseFloat(klant['longitude'])})
        setAdres(klant.adres.split(','))
        setCenterMap({lat: parseFloat(klant['latitude']), lng: parseFloat(klant['longitude'])})
    }, [])

    const onCloseClick = () => {
        setSelectedKlant({})
        setLocatie({})
        setAdres();
    }

    const onChangeInput = (event) => {
        setSelectedPlaats(event.target.value)
    }
    const onChangeSelect = (event) => {
        setSelectedAfstand(event.target.value)
    }

    const onZoekClick = () => {
        if (selectedPlaats !== undefined) {
            fromAddress(selectedPlaats)
                .then(({lat, lng}) => {
                    setCenterCircle({
                        lat: lat,
                        lng: lng
                    })
                    setCenterMap({
                        lat: lat,
                        lng: lng
                    })
                    if (selectedAfstand === 30) {
                        setZoomNumber(9)
                    }
                    if (selectedAfstand === 50) {
                        setZoomNumber(8)
                    }
                    if (selectedAfstand === 100) {
                        setZoomNumber(7)
                    }
                    setRadiusCircle(selectedAfstand * 1000)
                    setShowCircle(true)
                    setRerender(false)
                })
        }
    }

    useEffect(() => {
        setRerender(true)
    }, [rerender])

    // Create advanced markers with clustering
    useEffect(() => {
        if (!isLoaded || !mapRef.current || !klantenlijst || !window.google?.maps || !mapReady) return;

        // Cleanup previous markers
        markersRef.current.forEach(marker => marker.map = null);
        markersRef.current = [];
        if (clustererRef.current) {
            clustererRef.current.forEach(clusterer => clusterer.clearMarkers());
            clustererRef.current = [];
        }

        window.google.maps.importLibrary("marker").then(({ AdvancedMarkerElement }) => {
            const validKlanten = klantenlijst.filter(k => k.latitude && k.longitude);
            const markers = [];
            // Group markers per levc_num so each type gets its own clusterer/color
            // (clusters never mix markers from different types).
            const markersPerType = {};

            validKlanten.forEach(klant => {
                const lat = parseFloat(klant.latitude);
                const lng = parseFloat(klant.longitude);

                if (isNaN(lat) || isNaN(lng)) {
                    Bugsnag.notify(new Error("Invalid coordinates: " + JSON.stringify(klant)));
                    return;
                }

                const typeId = Number(klant.levc_num);
                const colorClasses = TYPE_COLOR_CLASSES[typeId] || TYPE_COLOR_CLASSES[10];

                const content = document.createElement('img');
                content.src = '/media/google_maps_icons/blue-dot.svg';
                // Recolor per bedrijf type: Klanten keeps the original svg colors,
                // Sales/Consultancy and Add-on Partners get a hue-rotated color.
                content.className = colorClasses.marker;

                const marker = new AdvancedMarkerElement({
                    map: mapRef.current,
                    position: { lat, lng },
                    content: content,
                    title: klant.naam,
                });

                marker.addListener('click', () => {
                    onSelect(klant);
                });

                markers.push(marker);
                if (!markersPerType[typeId]) markersPerType[typeId] = [];
                markersPerType[typeId].push(marker);
            });

            markersRef.current = markers;

            const clusterers = [];
            Object.entries(markersPerType).forEach(([typeId, typeMarkers]) => {
                if (typeMarkers.length === 0) return;
                const colorClasses = TYPE_COLOR_CLASSES[typeId] || TYPE_COLOR_CLASSES[10];

                clusterers.push(new MarkerClusterer({
                    map: mapRef.current,
                    markers: typeMarkers,
                    algorithmOptions: { radius: 130 },
                    renderer: {
                        render: ({ count, position }) => {
                            const style = clusterStyle[count >= 50 ? 2 : count >= 25 ? 1 : 0];
                            const div = document.createElement('div');
                            div.className = classes.clusterContainer;
                            div.style.width = `${style.width}px`;
                            div.style.height = `${style.height}px`;

                            const img = document.createElement('img');
                            img.src = style.url;
                            // These PNGs are a slightly different base blue than the marker
                            // pin, so they need their own (smaller) rotation per type.
                            img.className = `${classes.clusterImage} ${colorClasses.cluster}`;

                            const text = document.createElement('div');
                            text.textContent = count;
                            text.className = classes.clusterText;
                            text.style.color = style.textColor;

                            div.appendChild(img);
                            div.appendChild(text);

                            return new AdvancedMarkerElement({
                                position,
                                content: div,
                                map: mapRef.current,
                            });
                        },
                    },
                }));
            });

            clustererRef.current = clusterers;
        }).catch(error => {
            Bugsnag.notify(new Error("Failed to load marker library: " + error.message));
        });

    }, [isLoaded, klantenlijst, mapReady, onSelect]);


    const onWisClick = () => {
        setSelectedPlaats("")
        setSelectedAfstand(30)
        setCenterCircle(center)
        setRadiusCircle(0)
        setShowCircle(false)
        setZoomNumber(7)
        setRerender(false)
    }

    const circleElem = () => {
        return showCircle && <Circle
            center={centerCircle}
            radius={radiusCircle}
            options={{
                strokeColor: '#0000FF',
                strokeOpacity: 0.7,
                strokeWeight: 1,
                fillColor: '#0000FF',
                fillOpacity: 0.1,
                draggable: false,
                visible: showCircle
            }}
        />
    }


    return (
        <div id="klantenkaart">
            <div className={classes.kaartcontainer}>
                <h1 className={classes.headerKaartContainer}>{tekst("pagina:overmkg:klantenkaart:titel")}</h1>
                <p className={classes.introKaartContainer}>{tekst("pagina:overmkg:klantenkaart:intro")}</p>
                <div>
                    <div className={classes.inputFieldRow}>
                        <p className={classes.inputTextRow}>{tekst('pagina:overmkg:klantenkaart:subtitel')}</p>
                        <div className={classes.inputsFieldsRow}>
                            <div className={classes.inputFieldRp}>
                                <input value={selectedPlaats} type="text" placeholder={tekst('pagina:overmkg:klantenkaart:plaats')} className={classes.plaatsInput}
                                       onChange={(event) => onChangeInput(event)}
                                />
                                <select value={selectedAfstand} className={classes.selectField}
                                        onChange={(event) => onChangeSelect(event)}
                                >
                                    <option value={"30"}>&lt; 30 Km</option>
                                    <option value={"50"}>&lt; 50 Km</option>
                                    <option value={"100"}>&lt; 100 Km</option>
                                </select>
                            </div>
                            <div className={classes.buttonFieldRp}>
                                <button className={classes.buttonZoek}
                                        onClick={() => onZoekClick()}>
                                    {tekst('pagina:overmkg:klantenkaart:zoek')}
                                </button>
                                <button className={classes.buttonWissen}
                                        onClick={() => onWisClick()}>
                                    {tekst('pagina:overmkg:klantenkaart:wissen')}
                                </button>
                                <br/>
                            </div>
                        </div>
                    </div>
                    <div className={classes.typeFilterRow}>
                        <p className={classes.typeFilterLabel}>{tekst('pagina:overmkg:klantenkaart:filteroptype')}</p>
                        {BEDRIJF_TYPE_OPTIONS.map(({id, label}) => (
                            <label key={id} className={classes.typeFilterItem}>
                                <Switch
                                    checked={!!activeTypes[id]}
                                    onChange={() => onToggleType(id)}
                                    height={20}
                                    width={40}
                                    offColor={'#c4c4c4'}
                                    onColor={TYPE_SWITCH_COLORS[id]}
                                    checkedIcon={false}
                                    uncheckedIcon={false}
                                />
                                <span>{label}</span>
                            </label>
                        ))}
                    </div>
                    <div className={classes.kaartGrootte}>
                        {isLoaded ?
                            <GoogleMap
                                mapContainerStyle={containerStyle}
                                zoom={zoomNumber}
                                center={centerMap}
                                key={rerender}
                                onLoad={onMapLoad}
                                onUnmount={onMapUnmount}
                                options={{
                                    mapId: "2323bfb7fe6a04e06e16d707",
                                    colorScheme: google.maps.ColorScheme.DARK,
                                }}
                            >
                                {showCircle && circleElem()}

                                {selectedKlant?.latitude && adres[0] && (
                                    <InfoWindow
                                        position={locatie}
                                        clickable={true}
                                        onCloseClick={() => onCloseClick()}
                                    >
                                        <>
                                            <h5>{selectedKlant.naam}</h5>
                                            <p>{adres[0]}</p>
                                            <p>{adres[1]}</p>
                                            <p>{adres[2]}</p>
                                            {selectedKlant.url.startsWith('http://') ? (
                                                <a href={selectedKlant.url} target="_blank">{selectedKlant.url}</a>
                                            ) : (
                                                <a href={`http://${selectedKlant.url}`} target="_blank">{selectedKlant.url}</a>
                                            )}
                                        </>
                                    </InfoWindow>
                                )}
                            </GoogleMap>
                            : <></>}
                    </div>
                </div>

            </div>
            <div className={classes.klantenlijstContainer}>
                {klantenlijstKolommen.map((kolom, kolomIndex) => (
                    <div key={kolomIndex} className={classes.klantenlijstKolom}>
                        {kolom.map((kl) => (
                            kl.naam.length === 1
                                ? <h2 key={kl.id + kl.naam + kolomIndex}>{kl.naam}</h2>
                                : kl.url?.startsWith("http")
                                    ? <a key={kl.id + kl.naam + kolomIndex} href={kl.url} target={'_blank'} className={classes.linkstyling}>{kl.naam}</a>
                                    : <a key={kl.id + kl.naam + kolomIndex} href={'http://' + kl.url} target={'_blank'} className={classes.linkstyling}>{kl.naam}</a>
                        ))}
                    </div>
                ))}
            </div>

        </div>
    )
}
export default KlantenkaartOverMKG
